import React, { useMemo } from 'react'
import { AlertCircle, X } from 'lucide-react'
import { UserFile } from '__generated__/api-types-and-hooks'
import { AppFileFormatIcon } from 'components/Common/AppSvgIcons/AppFileFormatIcon'
import { AppFileMediaFormatIcon } from 'components/Common/AppSvgIcons/AppFileMediaFormatIcon'
import { AppBinIcon } from 'components/Common/AppSvgIcons/AppBinIcon'

interface AppFileCardProps {
  file: UserFile
  onRemove?: () => void
  showRemove?: boolean
  uploadProgress?: number // 0-100, undefined means not uploading
  isUploading?: boolean
  uploadError?: string // Error message like "File is too big" or "Failed to upload"
  onCancel?: () => void // Cancel upload in progress
  onRetry?: () => void // Retry failed upload
}

export const AppFileCard: React.FC<AppFileCardProps> = ({
  file,
  onRemove,
  showRemove = true,
  uploadProgress,
  isUploading = false,
  uploadError,
  onCancel,
  onRetry,
}) => {
  const { isMedia, sizeDisplay } = useMemo(() => {
    const mediaFileTypes = [
      'mp4',
      'mkv',
      'mp3',
      'wav',
      'avi',
      'mov',
      'wmv',
      'flv',
      'mpeg',
      'm4v',
      'webm',
    ]
    const ext = file?.name?.split('.').pop()?.toLowerCase() || ''
    const isMediaLocal = mediaFileTypes.some((t) => ext.includes(t))
    const sizeNum = Number(file?.size)
    let sizeDisplayLocal = ''
    if (sizeNum && sizeNum < 1000) sizeDisplayLocal = `${sizeNum} B`
    else if (sizeNum && sizeNum < 1_000_000) sizeDisplayLocal = `${Math.round(sizeNum / 1000)} KB`
    else if (sizeNum && sizeNum < 1_000_000_000)
      sizeDisplayLocal = `${Math.round(sizeNum / 1_000_000)} MB`
    else if (sizeNum && sizeNum < 1_000_000_000_000)
      sizeDisplayLocal = `${Math.round(sizeNum / 1_000_000_000)} GB`
    return { isMedia: isMediaLocal, sizeDisplay: sizeDisplayLocal }
  }, [file?.name, file?.size])

  return (
    <div className="flex items-center justify-between bg-app-white-100 border border-app-grey-20 rounded-lg p-4 shadow-lg">
      <a
        href={file.url || ''}
        target="_blank"
        rel="noreferrer"
        className="flex items-center gap-3 w-[90%]"
        onClick={(e) => (isUploading || uploadError) && e.preventDefault()}
      >
        <div className="flex-shrink-0">
          {uploadError ? (
            <div className="w-10 h-10 rounded-lg bg-app-red-25 flex items-center justify-center">
              <AlertCircle size={24} className="text-app-red-70" />
            </div>
          ) : isMedia ? (
            <AppFileMediaFormatIcon />
          ) : (
            <AppFileFormatIcon />
          )}
        </div>
        <div className="flex flex-col gap-1 min-w-0 flex-1">
          <span className="text-sm font-normal font-inter break-all">{file.name}</span>
          {uploadError ? (
            <span className="text-sm font-normal font-inter text-app-red-70">{uploadError}</span>
          ) : isUploading && uploadProgress !== undefined ? (
            <div className="flex items-center gap-2">
              <div className="flex-1 h-1.5 bg-app-grey-20 rounded-full overflow-hidden">
                <div
                  className="h-full bg-app-primary-60 rounded-full transition-all duration-300 ease-out"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
              <span className="text-xs text-app-grey-60 whitespace-nowrap">{uploadProgress}%</span>
            </div>
          ) : (
            <span className="text-sm font-normal font-inter text-app-grey-70">{sizeDisplay}</span>
          )}
        </div>
      </a>
      <div className="flex items-center gap-2">
        {/* Cancel button during upload */}
        {isUploading && onCancel && !uploadError && (
          <button
            type="button"
            onClick={onCancel}
            className="cursor-pointer p-1 hover:bg-app-grey-20 rounded transition-colors flex items-center justify-center"
            title="Cancel upload"
          >
            <X size={16} className="text-app-grey-60" />
          </button>
        )}
        {/* Retry button for failed uploads (not for "File is too big") */}
        {uploadError && onRetry && uploadError === 'Failed to upload' && (
          <button
            type="button"
            onClick={onRetry}
            className="px-3 py-1.5 bg-app-red-70 text-app-white-100 text-xs font-inter rounded hover:bg-app-red-80 transition-colors"
          >
            Retry
          </button>
        )}
        {/* Normal delete button */}
        {showRemove && onRemove && !isUploading && (
          <button type="button" onClick={onRemove} className="cursor-pointer">
            <AppBinIcon />
          </button>
        )}
      </div>
    </div>
  )
}
